'use client';

import React, { useState } from 'react';
import { Search, Bell, RefreshCw } from 'lucide-react';

interface TopbarProps {
  title: string;
  subtitle?: string;
}

const notifications = [
  { id: 'notif-1', text: 'New artisan application from Fatima Al-Rashdi', time: '2m ago', unread: true },
  { id: 'notif-2', text: 'Dispute #DIS-0041 escalated by customer', time: '18m ago', unread: true },
  { id: 'notif-3', text: 'Withdrawal request OMR 340 pending approval', time: '1h ago', unread: true },
  { id: 'notif-4', text: 'Job #JOB-1182 marked completed — Muscat', time: '2h ago', unread: false },
];

export default function Topbar({ title, subtitle }: TopbarProps) {
  const [showNotifs, setShowNotifs] = useState(false);
  const unreadCount = notifications.filter((n) => n.unread).length;

  return (
    <header className="h-16 bg-card border-b border-border flex items-center px-6 gap-4 sticky top-0 z-30 shrink-0">
      {/* Title */}
      <div className="flex-1 min-w-0">
        <h1 className="text-lg font-700 text-foreground truncate">{title}</h1>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 bg-muted rounded-lg px-3 py-2 w-64">
        <Search size={14} className="text-muted-foreground shrink-0" />
        <input
          type="text"
          placeholder="Search platform..."
          className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
        />
        <kbd className="hidden lg:inline text-xs text-muted-foreground bg-border px-1.5 py-0.5 rounded font-mono">
          ⌘K
        </kbd>
      </div>

      {/* Refresh */}
      <button className="btn-ghost p-2 rounded-lg" title="Refresh data">
        <RefreshCw size={16} />
      </button>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => setShowNotifs(!showNotifs)}
          className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-700 leading-none">
              {unreadCount}
            </span>
          )}
        </button>

        {showNotifs && (
          <div className="absolute right-0 top-12 w-80 card-elevated rounded-xl shadow-elevated z-50 overflow-hidden scale-in">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <span className="font-600 text-sm text-foreground">Notifications</span>
              <span className="text-xs text-accent font-600 cursor-pointer hover:underline">
                Mark all read
              </span>
            </div>
            <div className="max-h-72 overflow-y-auto scrollbar-thin">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-border/50 cursor-pointer hover:bg-muted transition-colors ${
                    n.unread ? 'bg-accent/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {n.unread && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-accent shrink-0" />
                    )}
                    {!n.unread && <span className="mt-1.5 w-2 h-2 shrink-0" />}
                    <div>
                      <p className="text-xs text-foreground leading-relaxed">{n.text}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{n.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 text-center">
              <span className="text-xs text-accent font-600 cursor-pointer hover:underline">
                View all notifications
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Last updated */}
      <div className="hidden lg:flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
        <span>Live · Updated just now</span>
      </div>
    </header>
  );
}